import type React from 'react'
import { useController } from 'react-hook-form'
import styled from 'styled-components'

import type { ButtonBaseProps } from '../Buttons'
import { InputLabel } from '../InputElements'
import { Typography } from '../Typography'
import { FormControl } from './FormControl'
import { FormHelperText } from './FormHelperText'

import { SelectableCardButton } from './styles'

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
`

interface ReactHookFormSelectCardFieldProps {
  className?: string
  disabled?: boolean
  name: string
  options: Array<{
    label: ButtonBaseProps['children']
    value: number | string | boolean
  }>
  isLabelHighlighted?: boolean
  label?: React.ReactNode
  required?: boolean
}

export function ReactHookFormSelectCardField({
  className,
  disabled,
  name,
  options,
  isLabelHighlighted,
  label,
  required,
}: ReactHookFormSelectCardFieldProps) {
  const {
    field: { onChange, value },
    fieldState: { error },
    formState: { isSubmitting },
  } = useController({ name })

  const hasError = !!error
  const isFieldDisabled = disabled || isSubmitting

  return (
    <FormControl className={className} error={hasError}>
      {label && (
        <InputLabel error={hasError} required={required} htmlFor={name} highlighted={isLabelHighlighted}>
          {label}
        </InputLabel>
      )}
      <CardsGrid>
        {options.map((option, index) => (
          <SelectableCardButton
            $isSelected={value === option.value}
            data-testid={`${name}-option-${option.value}`}
            disabled={isFieldDisabled}
            key={`${name}-option-${index}`}
            onClick={() => onChange(option.value)}
          >
            <Typography tag="span" size="navigation-md" color={value === option.value ? 'gray-80' : 'gray-70'}>
              {option.label}
            </Typography>
          </SelectableCardButton>
        ))}
      </CardsGrid>
      {error?.message && <FormHelperText>{error?.message}</FormHelperText>}
    </FormControl>
  )
}
